// components/Testimonials.tsx
"use client";

import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  useTheme,
  alpha,
  IconButton,
  Button,
  Chip,
  CircularProgress,
} from "@mui/material";
import Image from "next/image";
import {
  ChevronLeft,
  ChevronRight,
  PlayArrow,
  Pause,
} from "@mui/icons-material";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";

// Define the testimonial type based on your structure
interface Testimonial {
  id: string;
  logo: string;
  quote: string;
  client: string;
  role?: string;
  company?: string;
}

const Testimonials = () => {
  const theme = useTheme();
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch testimonials from Firestore
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setLoading(true);
        const q = query(
          collection(db, "testimonials"),
          where("isActive", "==", true),
          orderBy("createdAt", "desc")
        );

        const querySnapshot = await getDocs(q);
        const testimonialsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Testimonial[];

        setTestimonials(testimonialsData);
      } catch (error) {
        console.error("Fetch error:", error);
        setError("Failed to load testimonials.");
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isPlaying, testimonials.length]);

  const nextTestimonial = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToTestimonial = (index: number) => {
    setCurrentIndex(index);
  };

  const toggleAutoplay = () => {
    setIsPlaying(!isPlaying);
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || testimonials.length === 0) {
    return null; // Don't render section if no testimonials or error
  }

  return (
    <Box
      sx={{
        py: 8,
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h2"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              fontSize: { xs: "2.5rem", md: "3rem" },
            }}
          >
            What Our Clients Say
          </Typography>
          <Typography
            variant="h6"
            component={"p"}
            color="text.secondary"
            sx={{
              maxWidth: 600,
              mx: "auto",
              fontSize: { xs: "1.1rem", md: "1.25rem" },
              lineHeight: 1.6,
            }}
          >
            Don&apos;t just take our word for it. Here&apos;s what businesses
            we&apos;ve worked with have to say about our services.
          </Typography>
        </Box>

        {/* Carousel Container */}
        <Box
          sx={{
            position: "relative",
            height: { xs: 500, md: 450 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            my: 6,
          }}
        >
          {/* Stacked Cards */}
          {testimonials.map((testimonial, index) => {
            // Calculate position in the stack
            const position =
              (index - currentIndex + testimonials.length) %
              testimonials.length;
            const isActive = position === 0;
            const isNext = position === 1;
            const isPrevious = position === testimonials.length - 1;
            const isFar = position > 1 && position < testimonials.length - 1;

            // Calculate styles based on position
            let cardStyle: React.CSSProperties = {};

            if (isActive) {
              cardStyle = {
                zIndex: 30,
                transform: "translateX(0) scale(1)",
                opacity: 1,
                filter: "none",
              };
            } else if (isNext) {
              cardStyle = {
                zIndex: 20,
                transform: "translateX(120px) scale(0.9)",
                opacity: 0.7,
                filter: "blur(1px)",
              };
            } else if (isPrevious) {
              cardStyle = {
                zIndex: 20,
                transform: "translateX(-120px) scale(0.9)",
                opacity: 0.7,
                filter: "blur(1px)",
              };
            } else if (isFar) {
              cardStyle = {
                zIndex: 10,
                transform:
                  position > 1
                    ? "translateX(200px) scale(0.8)"
                    : "translateX(-200px) scale(0.8)",
                opacity: 0.3,
                filter: "blur(2px)",
              };
            }

            return (
              <Card
                key={testimonial.id}
                sx={{
                  position: "absolute",
                  width: { xs: "90%", md: "70%", lg: "60%" },
                  maxWidth: 600,
                  padding: { xs: 3, md: 4 },
                  textAlign: "center",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  background: "white",
                  transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  height: "auto",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  cursor: isActive ? "default" : "pointer",
                  "&:hover": isActive
                    ? {}
                    : {
                        transform: isNext
                          ? "translateX(110px) scale(0.92)"
                          : isPrevious
                            ? "translateX(-110px) scale(0.92)"
                            : (cardStyle.transform as string),
                        opacity:
                          isNext || isPrevious
                            ? 0.8
                            : (cardStyle.opacity as number),
                      },
                  ...cardStyle,
                }}
                onClick={() => !isActive && goToTestimonial(index)}
              >
                {/* Service Tag */}
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={`Testimonial ${index + 1}`}
                    size="small"
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  />
                </Box>

                {/* Client Logo */}
                <Box sx={{ mb: 3 }}>
                  {testimonial.logo ? (
                    <Image
                      width={80}
                      height={80}
                      sizes="(max-width: 768px) 80px, 80px"
                      src={testimonial.logo}
                      alt={testimonial.client}
                      style={{
                        borderRadius: "8px",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "1.5rem",
                      }}
                    >
                      {testimonial.client?.charAt(0)?.toUpperCase() || "C"}
                    </Box>
                  )}
                </Box>

                {/* Quote */}
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{
                    fontStyle: "italic",
                    lineHeight: 1.6,
                    color: "text.primary",
                    mb: 3,
                    flexGrow: 1,
                    display: "-webkit-box",
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    "&:before": {
                      content: '"“"',
                      fontSize: "2rem",
                      color: theme.palette.primary.main,
                      lineHeight: 0,
                      mr: 1,
                    },
                    "&:after": {
                      content: '"”"',
                      fontSize: "2rem",
                      color: theme.palette.primary.main,
                      lineHeight: 0,
                      ml: 1,
                    },
                  }}
                >
                  {testimonial.quote}
                </Typography>

                {/* Client Info */}
                <Box>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    {testimonial.client}
                  </Typography>
                  {testimonial.role && testimonial.company && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {testimonial.role}, {testimonial.company}
                    </Typography>
                  )}
                </Box>
              </Card>
            );
          })}

          {/* Navigation Arrows */}
          <IconButton
            aria-label="Previous Testimonial"
            onClick={prevTestimonial}
            sx={{
              position: "absolute",
              left: { xs: 10, md: 50 },
              zIndex: 40,
              backgroundColor: "white",
              boxShadow: 3,
              "&:hover": {
                backgroundColor: theme.palette.primary.main,
                color: "white",
              },
            }}
          >
            <ChevronLeft />
          </IconButton>

          <IconButton
            aria-label="Next Testimonial"
            onClick={nextTestimonial}
            sx={{
              position: "absolute",
              right: { xs: 10, md: 50 },
              zIndex: 40,
              backgroundColor: "white",
              boxShadow: 3,
              "&:hover": {
                backgroundColor: theme.palette.primary.main,
                color: "white",
              },
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>

        {/* Controls */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 3,
            mt: 4,
          }}
        >
          {/* Dots Indicator */}
          <Box sx={{ display: "flex", gap: 2 }}>
            {testimonials.map((_, index) => (
              <Button
                aria-label={`Go to testimonial ${index + 1}`}
                key={index}
                onClick={() => goToTestimonial(index)}
                sx={{
                  minWidth: 12,
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  padding: 0,
                  backgroundColor:
                    currentIndex === index
                      ? theme.palette.primary.main
                      : alpha(theme.palette.primary.main, 0.3),
                  "&:hover": {
                    backgroundColor:
                      currentIndex === index
                        ? theme.palette.primary.dark
                        : alpha(theme.palette.primary.main, 0.5),
                  },
                }}
              />
            ))}
          </Box>

          {/* Play/Pause Button */}
          <IconButton
            aria-label={isPlaying ? "Pause Autoplay" : "Play Autoplay"}
            onClick={toggleAutoplay}
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
              },
            }}
          >
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>
        </Box>

        {/* Progress Bar */}
        <Box
          sx={{
            width: "100%",
            height: 4,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            borderRadius: 2,
            mt: 3,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              backgroundColor: theme.palette.primary.main,
              width: `${((currentIndex + 1) / testimonials.length) * 100}%`,
              transition: "width 0.3s ease",
              borderRadius: 2,
            }}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default Testimonials;
